import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import CaseCard from "@/components/feature/CaseCard";
import { useSeo } from "@/hooks/useSeo";
import { cases } from "@/mocks/cases";
import { categories } from "@/mocks/site";
import CaseFilterBar from "@/pages/cases/components/CaseFilterBar";

export default function CasesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";
  const query = searchParams.get("q") ?? "";
  const sort = searchParams.get("sort") ?? "latest";

  const updateParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === "all") {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    const result = cases.filter((item) => {
      const matchCategory = activeCategory === "all" || item.category === activeCategory;
      if (!matchCategory) return false;
      if (!keyword) return true;
      const haystack = [
        item.title,
        item.summary,
        item.categoryLabel,
        item.author,
        ...item.tags,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(keyword);
    });

    return [...result].sort((a, b) => {
      if (sort === "hot") return b.heat - a.heat;
      if (sort === "title") return a.title.localeCompare(b.title, "zh-Hans-CN");
      return a.date < b.date ? 1 : -1;
    });
  }, [activeCategory, query, sort]);

  const activeLabel =
    activeCategory === "all"
      ? "全部档案"
      : categories.find((item) => item.key === activeCategory)?.label ?? "全部档案";

  useSeo({
    title: `调查档案库 - ${activeLabel}｜黑暗爆料`,
    description:
      "黑暗爆料调查档案库，收录消费陷阱、行业潜规则、暗箱操作、灰色地带与职场招聘等方向的调查笔记，支持按分类筛选与关键词搜索，方便对照自身经历。",
    keywords: "黑暗爆料,调查档案,行业黑幕,潜规则,暗箱操作,灰色地带,消费陷阱",
    path: "/cases",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "黑暗爆料调查档案库",
        description:
          "收录消费陷阱、行业潜规则、暗箱操作、灰色地带与职场招聘方向的调查笔记。",
        url: "https://i2785.cn/cases",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://i2785.cn/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "调查档案",
            item: "https://i2785.cn/cases",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        numberOfItems: filtered.length,
        itemListElement: filtered.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `https://i2785.cn/cases/${item.id}`,
          name: item.title,
        })),
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="pt-16 md:pt-[72px]">
        <div className="bg-foreground-950 text-background-50">
          <div className="max-w-[1240px] mx-auto px-4 md:px-10 py-12 md:py-16">
            <div className="[&_*]:!text-background-200">
              <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "调查档案" }]} />
            </div>
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-bold text-background-50">
              调查档案库
            </h1>
            <p className="mt-4 text-sm md:text-base text-background-200 leading-relaxed max-w-2xl">
              每一篇档案都按「谁先开口、谁签字、谁收钱」的顺序拆解一个具体环节。你可以按分类浏览，也可以直接搜索你正在经历的那个词。
            </p>
          </div>
        </div>

        <div className="max-w-[1240px] mx-auto px-4 md:px-10 py-10 md:py-14">
          <CaseFilterBar
            activeCategory={activeCategory}
            onCategoryChange={(value) => updateParams({ category: value })}
            query={query}
            onQueryChange={(value) => updateParams({ q: value })}
            sort={sort}
            onSortChange={(value) => updateParams({ sort: value })}
            total={filtered.length}
          />

          <div className="mt-8 flex items-center justify-between">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              {activeLabel}
              <span className="ml-2 text-sm font-normal text-foreground-500">
                {filtered.length} 篇
              </span>
            </h2>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
              {filtered.map((item) => (
                <CaseCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 px-6 text-center">
              <span className="inline-flex w-14 h-14 items-center justify-center rounded-md bg-background-50 text-2xl text-foreground-400 mb-4">
                <i className="ri-search-2-line" aria-hidden="true"></i>
              </span>
              <p className="font-heading text-lg font-semibold text-foreground-950">
                没有找到匹配的档案
              </p>
              <p className="mt-2 text-sm text-foreground-600">
                换个关键词试试，或者切换分类查看全部内容。
              </p>
              <button
                type="button"
                onClick={() => setSearchParams({}, { replace: true })}
                className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line" aria-hidden="true"></i>
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}